/* index.js — /natal-chart page controller.
 *
 * Entry point of the module graph. Loaded via @moduleGraph so the version sits
 * in the PATH and every relative import below inherits it — a query string
 * would not, and the submodules would hang in cache for months.
 */

import { buildChart, AYANAMSHAS } from './chart.js';
import { ASPECTS } from './aspects.js';
import { DIGNITY_POINTS as DIGNITY_PTS } from './dignities.js';
import { assess, PRECISIONS, VERDICT, unknownTimeSummary } from './uncertainty.js';
import { localToUtc, explainOffset, formatOffset, formatDelta, zoneIsKnown } from './timeresolve.js';
import { HOUSE_SYSTEMS, allSystems } from './houses.js';
import { renderWheel, standaloneWheelSvg, primeWheelCss } from './wheel.js';
import { glyphSvg, SIGN_KEYS } from './glyphs.js';
import { searchBirthPlace, flag } from './places.js';
import { norm360, angleDiff } from './ephemeris.js';

const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

/* Write into a slot that may legitimately be absent.
 *
 * The page HTML and this module are cached independently: a returning visitor
 * can be served yesterday's markup with today's script for as long as the page
 * cache holds. When that happened, the first missing container threw inside
 * paint() and every table after it — positions, houses, aspects — was left
 * blank, so a layout change silently emptied the results for cached visitors.
 * A slot that is not in the document is skipped instead. */
const fill = (sel, html) => { const el = $(sel); if (el) el.innerHTML = html; return el; };

const T = window.__NATAL_I18N || {};
const t = (k, vars) => {
    let s = T[k] ?? k;
    // split/join, а не replaceAll: последний — метод ES2021 (Safari 13.4+), а
    // сборка целится в ES2019, и МЕТОДЫ минификатор не полифилит — только
    // синтаксис. На iOS 12.4 это «s.replaceAll is not a function» и мёртвая
    // страница (Sentry GF). Для строкового разделителя split/join равнозначен.
    if (vars) for (const [n, v] of Object.entries(vars)) s = s.split(':' + n).join(v);
    return s;
};
const SLUG = 'natal-chart';

/* ── formatting ─────────────────────────────────────────────────────────── */

const signName = (i) => t('sign_' + SIGN_KEYS[i]);
const bodyName = (k) => t('body_' + k);

function fmtLon(lon) {
    const l = norm360(lon);
    const s = Math.floor(l / 30);
    const d = l - s * 30;
    const deg = Math.floor(d);
    const min = Math.floor((d - deg) * 60);
    return { deg, min, sign: s, text: `${deg}°${String(min).padStart(2, '0')}′` };
}
const fmtFull = (lon) => {
    const f = fmtLon(lon);
    return `${f.text} ${signName(f.sign)}`;
};
const esc = (s) => String(s).replace(/[<>&"]/g, m => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[m]));

/* ── state ──────────────────────────────────────────────────────────────── */

const state = {
    place: null,          // { name, lat, lng, tz, cc, region, kind, years }
    resolved: null,       // localToUtc result
    chosenInstant: null,  // when ambiguous, which of the two
    chart: null,
    uncertainty: null,
    settings: {
        system: 'placidus',
        zodiac: 'tropical',
        ayanamsa: 'lahiri',
        orbScale: 1,
        minorAspects: false,
        modernRulers: false,
    },
};

/* ── place picker ───────────────────────────────────────────────────────── */

function initPlacePicker() {
    const input = $('#nt-place');
    const box = $('#nt-place-results');
    if (!input) return;
    let timer = null;

    const birthYear = () => {
        const v = $('#nt-date')?.value;
        return v ? +v.slice(0, 4) : null;
    };

    const close = () => { box.hidden = true; box.innerHTML = ''; };

    const render = (items) => {
        if (!items.length) { close(); return; }
        box.innerHTML = items.map((it, i) => {
            const isHist = it.kind === 'historic';
            const years = isHist
                ? `${it.years[0] || '…'}–${it.years[1] || t('place_now')}`
                : '';
            const sub = [it.region, it.cc].filter(Boolean).join(', ');
            return `<button type="button" class="nt-place-item${isHist ? ' is-historic' : ''}" data-i="${i}">
                <span class="nt-flag">${flag(it.cc)}</span>
                <span class="nt-place-main">
                    <span class="nt-place-name">${esc(it.name)}${isHist
                        ? ` <span class="nt-hist-tag">${esc(t('place_was', { name: it.query, years }))}</span>` : ''}</span>
                    <span class="nt-place-sub">${esc(sub)} · ${esc(it.tz)}</span>
                </span>
            </button>`;
        }).join('');
        box.hidden = false;
        $$('.nt-place-item', box).forEach(b => b.addEventListener('click', () => {
            pickPlace(items[+b.dataset.i]);
            close();
        }));
    };

    input.addEventListener('input', () => {
        clearTimeout(timer);
        // Typing again invalidates the previous choice — otherwise a stale
        // selection silently survives while the field shows a different city.
        if (state.place && input.value.trim() !== state.place.name) {
            state.place = null;
            $('#nt-place-note').hidden = true;
        }
        const q = input.value.trim();
        if (q.length < 2) { close(); return; }
        timer = setTimeout(async () => {
            try { render(await searchBirthPlace(q, birthYear())); }
            catch { close(); }
        }, 160);
    });
    // Leaving the field with a typed name confirms it, so the note below shows
    // the resolved place before the user ever presses the button.
    input.addEventListener('blur', () => {
        setTimeout(close, 180);
        if (!state.place && input.value.trim().length >= 2) resolveTypedPlace({ quiet: true });
    });
    input.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        const first = $('.nt-place-item', box);
        if (first) { first.click(); } else { resolveTypedPlace({ quiet: true }); }
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
}

/**
 * Resolve whatever is typed in the place field, without requiring a click.
 *
 * The first version only ever set the place from a click on a suggestion. Type
 * "Казань", press the button, and the tool answered "choose a birth place from
 * the list" — while the list plainly contained Казань. The city was right, the
 * data was right, and the tool said no. That is not a validation, it is a
 * trap: most people type the name and press the button.
 *
 * Now the typed text is resolved on its own. Only genuine ambiguity is put back
 * to the user, and even then as a visible choice rather than a refusal.
 */
async function resolveTypedPlace({ quiet = false } = {}) {
    const input = $('#nt-place');
    const q = input.value.trim();
    if (!q) return false;

    let matches = [];
    try { matches = await searchBirthPlace(q, birthYearFromForm()); } catch { /* offline */ }
    if (!matches.length) {
        if (!quiet) flash(t('err_place_unknown', { name: q }));
        return false;
    }

    const norm = (s) => String(s).toLowerCase().replace(/[ёë]/g, 'е').replace(/[\s'’-]/g, '');
    const exact = matches.filter(m => norm(m.name) === norm(q)
        || (m.kind === 'historic' && norm(m.query) === norm(q)));

    // One obvious answer, or one clearly biggest city among same-named ones —
    // take it and SAY which one was taken.
    const pool = exact.length ? exact : matches;
    const top = pool[0];
    const rival = pool[1];
    const decisive = pool.length === 1
        || (top.kind === 'historic' && !rival?.exact)
        || ((top.pop || 0) >= (rival?.pop || 0) * 8);

    if (decisive) {
        pickPlace(top);
        return true;
    }

    // Genuinely ambiguous: show the candidates instead of a bare refusal.
    const box = $('#nt-place-results');
    box.innerHTML = `<div class="nt-place-ask">${esc(t('place_pick_which', { name: q }))}</div>`
        + pool.slice(0, 5).map((it, i) => `<button type="button" class="nt-place-item" data-i="${i}">
            <span class="nt-flag">${flag(it.cc)}</span>
            <span class="nt-place-main"><span class="nt-place-name">${esc(it.name)}</span>
            <span class="nt-place-sub">${esc([it.region, it.cc].filter(Boolean).join(', '))} · ${esc(it.tz)}</span></span>
        </button>`).join('');
    box.hidden = false;
    $$('.nt-place-item', box).forEach(b => b.addEventListener('click', () => {
        pickPlace(pool[+b.dataset.i]);
        box.hidden = true;
        run();
    }));
    if (!quiet) flash(t('err_place_ambiguous'));
    return false;
}

function birthYearFromForm() {
    const v = $('#nt-date')?.value;
    return v ? +v.slice(0, 4) : null;
}

function pickPlace(p) {
    /* An IANA zone the browser does not recognise would otherwise throw deep
     * inside Intl formatting, long after the choice was made and with a message
     * that says nothing about the place. `zoneIsKnown` existed for exactly this
     * and was imported but never called. A stale or misspelled zone falls back
     * to UTC and SAYS so, rather than taking the chart down. */
    if (p.tz && !zoneIsKnown(p.tz)) {
        window.__handleError?.(new Error('unknown time zone: ' + p.tz), SLUG);
        p = { ...p, tz: 'UTC', tzFallback: true };
    }
    state.place = p;
    $('#nt-place').value = p.name;
    const note = $('#nt-place-note');
    let html = `${flag(p.cc)} ${esc(p.name)} · ${p.lat.toFixed(4)}, ${p.lng.toFixed(4)} · ${esc(p.tz)}`;
    if (p.tzFallback) html += `<div class="nt-warn">${esc(t('tz_unknown'))}</div>`;

    if (p.kind === 'historic') {
        const years = `${p.years[0] || '…'}–${p.years[1] || t('place_now')}`;
        html += `<div class="nt-hint">${esc(t('place_renamed', { old: p.query, years, name: p.name }))}</div>`;
    }
    /* A name that is also a live city elsewhere is called out explicitly: the
     * two can be hours apart, and picking the wrong one produces a chart that
     * looks entirely normal. */
    if (p.collides && p.collides.length) {
        html += `<div class="nt-warn">${esc(t('place_collision'))} `
            + p.collides.map(c => `<button type="button" class="nt-linkbtn" data-lat="${c.lat}" data-lng="${c.lng}"
                 data-tz="${esc(c.tz)}" data-name="${esc(c.name)}" data-cc="${esc(c.cc)}"
                 data-region="${esc(c.region || '')}">${esc(c.name)}, ${esc(c.region || c.cc)} (${esc(c.tz)})</button>`).join(' · ')
            + '</div>';
    }
    note.innerHTML = html;
    note.hidden = false;
    $$('.nt-linkbtn', note).forEach(b => b.addEventListener('click', () => {
        pickPlace({
            kind: 'city', name: b.dataset.name, lat: +b.dataset.lat, lng: +b.dataset.lng,
            tz: b.dataset.tz, cc: b.dataset.cc, region: b.dataset.region,
        });
    }));
    window.__stage?.('place_chosen');
}

/* ── time resolution ────────────────────────────────────────────────────── */

function resolveTime() {
    const dv = $('#nt-date').value;
    const unknown = $('#nt-unknown-time').checked;
    if (!dv || !state.place) return null;
    const [y, mo, d] = dv.split('-').map(Number);

    if (unknown) {
        return { kind: 'unknown-time', y, mo, d };
    }
    const tv = $('#nt-time').value || '12:00';
    const [h, mi] = tv.split(':').map(Number);
    return { ...localToUtc(state.place.tz, y, mo, d, h, mi), y, mo, d, h, mi };
}

function renderTimeNotice(r) {
    const el = $('#nt-time-notice');
    if (!r) { el.hidden = true; return; }
    const p = state.place;

    if (r.kind === 'unknown-time') {
        el.className = 'nt-notice nt-notice-info';
        el.innerHTML = `<strong>${esc(t('tn_unknown_title'))}</strong><p>${esc(t('tn_unknown_body'))}</p>`;
        el.hidden = false;
        return;
    }

    if (r.kind === 'gap') {
        el.className = 'nt-notice nt-notice-warn';
        const sug = new Date(r.gapSuggestion);
        const local = new Intl.DateTimeFormat(window.__LOCALE || 'ru', {
            timeZone: p.tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
        }).format(sug);
        el.innerHTML = `<strong>${esc(t('tn_gap_title'))}</strong>`
            + `<p>${esc(t('tn_gap_body', { time: `${String(r.h).padStart(2, '0')}:${String(r.mi).padStart(2, '0')}` }))}</p>`
            + `<button type="button" class="nt-btn nt-btn-sm" id="nt-gap-fix">${esc(t('tn_gap_use', { time: local }))}</button>`;
        el.hidden = false;
        $('#nt-gap-fix')?.addEventListener('click', () => {
            $('#nt-time').value = local;
            run();
        });
        return;
    }

    if (r.kind === 'ambiguous') {
        el.className = 'nt-notice nt-notice-warn';
        const opts = r.instants.map((ms, i) => {
            const off = formatOffset(r.offsets[i]);
            const on = state.chosenInstant === ms ? ' is-on' : '';
            return `<button type="button" class="nt-chip${on}" data-ms="${ms}">${esc(off)} — ${esc(new Date(ms).toISOString().slice(11, 16))} UTC</button>`;
        }).join('');
        el.innerHTML = `<strong>${esc(t('tn_ambiguous_title'))}</strong>`
            + `<p>${esc(t('tn_ambiguous_body'))}</p><div class="nt-chips">${opts}</div>`;
        el.hidden = false;
        $$('.nt-chip', el).forEach(b => b.addEventListener('click', () => {
            state.chosenInstant = +b.dataset.ms;
            run();
        }));
        return;
    }

    /* Normal case: show the offset and where it came from. Not "UTC+5" but the
     * three independent parts, each checkable. */
    const ms = r.instants[0];
    const e = explainOffset(p.tz, ms, p.lng);
    const rows = [
        [t('off_solar'), formatOffset(e.solarMs), t('off_solar_hint')],
        [t('off_admin'), formatDelta(e.administrativeMs, t('unit_h'), t('unit_min')), t('off_admin_hint')],
        [t('off_dst'), formatDelta(e.dstMs, t('unit_h'), t('unit_min')), e.isSummerTime ? t('off_dst_on') : t('off_dst_off')],
    ];
    el.className = 'nt-notice nt-notice-ok';
    el.innerHTML = `<strong>${esc(t('tn_offset_title', { offset: formatOffset(e.totalMs) }))}</strong>`
        + `<table class="nt-offtable">${rows.map(([a, b, c]) =>
            `<tr><td>${esc(a)}</td><td class="nt-num">${esc(b)}</td><td class="nt-dim">${esc(c)}</td></tr>`).join('')}</table>`
        + `<p class="nt-dim">${esc(t('tn_offset_utc', { utc: new Date(ms).toISOString().replace('T', ' ').slice(0, 16) }))}</p>`;
    el.hidden = false;
}

/* ── results ────────────────────────────────────────────────────────────── */

const verdictClass = { stable: 'ok', boundary: 'warn', unknown: 'bad' };

function badge(v) {
    return `<span class="nt-badge nt-badge-${verdictClass[v] || 'ok'}">${esc(t('verdict_' + v))}</span>`;
}

function renderBigThree(chart, unc) {
    const items = [
        ['sun', chart.points.sun],
        ['moon', chart.points.moon],
        ['asc', chart.angles ? { lon: chart.angles.asc } : null],
    ].filter(([, p]) => p);

    return `<div class="nt-bigthree">` + items.map(([k, p]) => {
        const u = k === 'asc' ? unc?.angles?.asc : unc?.points?.[k];
        const f = fmtLon(p.lon);
        const shaky = u && u.verdict !== VERDICT.STABLE;
        return `<div class="nt-big">
            <div class="nt-big-glyph">${glyphSvg(k, 34)}</div>
            <div class="nt-big-label">${esc(k === 'asc' ? t('angle_asc') : bodyName(k))}</div>
            <div class="nt-big-sign">${esc(signName(f.sign))}</div>
            <div class="nt-big-deg">${esc(f.text)}</div>
            ${u ? `<div class="nt-big-verdict">${badge(u.verdict)}${shaky && u.signs?.length > 1
                ? `<span class="nt-dim"> ${esc(u.signs.map(s => t('sign_' + s)).join(' / '))}</span>` : ''}</div>` : ''}
        </div>`;
    }).join('') + '</div>';
}

/* Moon phase drawn as an actual disc rather than named in words: the shape is
 * read instantly and survives translation. Geometry is a circle plus an ellipse
 * whose x-radius tracks the terminator, which is what a lit phase really is. */
function moonPhaseSvg(angle, size = 34) {
    const r = size / 2 - 1;
    const k = Math.cos(angle * Math.PI / 180);   // +1 new, -1 full
    const waxing = angle < 180;
    const rx = Math.abs(k) * r;
    // Lit side flips at half-moon, so the two arcs swap sweep direction.
    const sweepOuter = waxing ? 1 : 0;
    const sweepInner = k > 0 ? (waxing ? 0 : 1) : (waxing ? 1 : 0);
    return `<svg class="nt-moonphase" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" aria-hidden="true">`
        + `<circle cx="${size / 2}" cy="${size / 2}" r="${r}" class="nt-moon-dark"/>`
        + `<path class="nt-moon-lit" d="M ${size / 2} ${size / 2 - r} `
        + `A ${r} ${r} 0 0 ${sweepOuter} ${size / 2} ${size / 2 + r} `
        + `A ${rx.toFixed(2)} ${r} 0 0 ${sweepInner} ${size / 2} ${size / 2 - r} Z"/>`
        + `<circle cx="${size / 2}" cy="${size / 2}" r="${r}" class="nt-moon-rim"/></svg>`;
}

function renderFacts(chart) {
    const items = [];

    const mp = chart.moonPhase;
    if (mp) {
        items.push(`<div class="nt-fact">
            <div class="nt-fact-icon">${moonPhaseSvg(mp.angle)}</div>
            <div class="nt-fact-body">
                <div class="nt-fact-label">${esc(t('moon_phase'))}</div>
                <div class="nt-fact-value">${esc(t('phase_' + mp.name))}</div>
                <div class="nt-fact-sub">${esc(t('moon_illum', { pct: Math.round(mp.illumination * 100) }))}</div>
            </div></div>`);
    }

    const d = chart.dominants;
    if (d?.planet) {
        items.push(`<div class="nt-fact">
            <div class="nt-fact-icon">${glyphSvg(d.planet, 28)}</div>
            <div class="nt-fact-body">
                <div class="nt-fact-label">${esc(t('dominant_planet'))}</div>
                <div class="nt-fact-value">${esc(bodyName(d.planet))}</div>
                <div class="nt-fact-sub">${esc(t('dominant_sign'))}: ${esc(signName(d.sign))}</div>
            </div></div>`);
    }

    if (chart.shape) {
        items.push(`<div class="nt-fact">
            <div class="nt-fact-icon nt-fact-glyph">◍</div>
            <div class="nt-fact-body">
                <div class="nt-fact-label">${esc(t('chart_shape'))}</div>
                <div class="nt-fact-value">${esc(t('shape_' + chart.shape))}</div>
                ${chart.ruler ? `<div class="nt-fact-sub">${esc(t('chart_ruler'))}: ${esc(bodyName(chart.ruler))}</div>` : ''}
            </div></div>`);
    }

    if (chart.sect) {
        items.push(`<div class="nt-fact">
            <div class="nt-fact-icon nt-fact-glyph">${chart.sect.diurnal ? '☀' : '☾'}</div>
            <div class="nt-fact-body">
                <div class="nt-fact-label">${esc(t('sect_label'))}</div>
                <div class="nt-fact-value">${esc(chart.sect.diurnal ? t('sect_day') : t('sect_night'))}</div>
            </div></div>`);
    }

    return items.length
        ? `<div class="nt-facts">${items.join('')}</div>
           <p class="nt-method">${esc(t('dominant_method'))}</p>`
        : '';
}

function renderPositions(chart, unc) {
    /* The angles belong in the table, not only on the wheel: in whole-sign mode
     * cusp 10 is not the MC, so without these rows the MC's actual degree is
     * printed nowhere the user can copy it. */
    const order = ['sun', 'moon', 'mercury', 'venus', 'mars', 'jupiter', 'saturn',
        'uranus', 'neptune', 'pluto', 'chiron', 'northNode', 'southNode', 'meanNode',
        'lilith', 'ceres', 'pallas', 'juno', 'vesta', 'fortune', 'vertex', 'asc', 'mc'];
    const nak = chart.nakshatras;
    const rows = order.filter(k => chart.points[k]).map(k => {
        const p = chart.points[k];
        const u = unc?.points?.[k];
        const dig = chart.dignities.find(d => d.planet === k);
        const n = nak?.[k];
        return `<tr>
            <td class="nt-cell-body">${glyphSvg(k, 18)}<span>${esc(bodyName(k))}</span></td>
            <td>${esc(fmtFull(p.lon))}</td>
            ${nak ? `<td class="nt-dim">${n ? esc(t('nak_' + n.key)) + ' ' + n.pada : ''}</td>` : ''}
            <td class="nt-num">${p.house ?? '—'}</td>
            <td class="nt-num">${p.retrograde ? '<span class="nt-r">R</span>' : ''}</td>
            <td class="nt-num nt-dim">${p.speed != null ? p.speed.toFixed(3) + '°' : ''}</td>
            <td class="nt-num nt-dim">${p.declination != null ? p.declination.toFixed(2) + '°' : ''}${
                p.outOfBounds ? ` <span class="nt-oob" title="${esc(t('oob_hint'))}">${esc(t('oob'))}</span>` : ''}</td>
            <td>${dig?.dignity ? `<span class="nt-dig nt-dig-${dig.dignity}">${esc(t('dig_' + dig.dignity))}</span>` : ''}</td>
            <td>${u ? badge(u.verdict) : ''}</td>
        </tr>`;
    }).join('');

    /* A body that never loaded must SAY so. The asteroid tables are fetched
     * separately, and when that fetch fails the rows simply were not emitted —
     * indistinguishable from a tool that never offered Chiron or Ceres. */
    const missing = chart.tablesMissing
        ? `<div class="nt-notice nt-notice-warn" style="margin-bottom:10px">${esc(t('bodies_unavailable'))}</div>`
        : '';

    return missing + `<table class="nt-table"><thead><tr>
        <th>${esc(t('col_body'))}</th><th>${esc(t('col_position'))}</th>
        ${nak ? `<th>${esc(t('col_nakshatra'))}</th>` : ''}
        <th>${esc(t('col_house'))}</th>
        <th>${esc(t('col_r'))}</th><th>${esc(t('col_speed'))}</th>
        <th>${esc(t('col_declination'))}</th><th>${esc(t('col_dignity'))}</th>
        <th>${esc(t('col_certainty'))}</th></tr></thead><tbody>${rows}</tbody></table>`;
}

/* ── the five-fold dignity table ─────────────────────────────────────────────
 *
 * Reference sites print all five essential dignities with a Ptolemaic score; we
 * printed one. The weights are stated in the footnote because a bare total
 * invites comparison with a site that scored it differently.
 */
function renderDignities(chart) {
    const rows = chart.dignities.filter(d => d.full);
    if (!rows.length) return `<p class="nt-dim">${esc(t('dignities_need_time'))}</p>`;

    const cell = (planet, ruler, held, key) => {
        const own = ruler === planet;
        return `<td class="${own ? 'nt-dig-own' : 'nt-dim'}">${esc(bodyName(ruler))}${
            own ? ` <span class="nt-dig-pt">+${DIGNITY_PTS[key]}</span>` : ''}</td>`;
    };

    const body = rows.map(d => {
        const f = d.full;
        const major = f.held.find(h => ['domicile', 'exaltation', 'detriment', 'fall'].includes(h));
        const peregrine = f.held.includes('peregrine');
        return `<tr>
            <td class="nt-cell-body">${glyphSvg(d.planet, 17)}<span>${esc(bodyName(d.planet))}</span></td>
            <td>${major ? `<span class="nt-dig nt-dig-${major}">${esc(t('dig_' + major))}</span>`
                : (peregrine ? `<span class="nt-dig">${esc(t('dig_peregrine'))}</span>` : '')}</td>
            ${cell(d.planet, f.rulers.triplicity, f.held, 'triplicity')}
            ${cell(d.planet, f.rulers.term, f.held, 'term')}
            ${cell(d.planet, f.rulers.face, f.held, 'face')}
            <td class="nt-num"><strong>${f.score > 0 ? '+' : ''}${f.score}</strong></td>
        </tr>`;
    }).join('');

    const rec = chart.receptions?.length
        ? `<p style="margin-top:10px">${chart.receptions.map(r =>
            `<span class="nt-tag">${esc(t('reception'))}: ${esc(bodyName(r.a))} ⇄ ${esc(bodyName(r.b))} (${esc(t('dig_' + r.by))})</span>`).join(' ')}</p>`
        : '';

    return `<table class="nt-table nt-table-compact"><thead><tr>
        <th>${esc(t('col_body'))}</th><th>${esc(t('col_dignity'))}</th>
        <th>${esc(t('col_triplicity'))}</th><th>${esc(t('col_term'))}</th>
        <th>${esc(t('col_face'))}</th><th>${esc(t('col_score'))}</th>
        </tr></thead><tbody>${body}</tbody></table>${rec}
        <p class="nt-method">${esc(t('dignity_method'))}</p>`;
}

/* ── fixed stars ─────────────────────────────────────────────────────────── */
function renderStars(chart) {
    const hits = chart.stars || [];
    if (!hits.length) return `<p class="nt-dim">${esc(t('stars_none'))}</p>`;
    const rows = hits.map(h => `<tr>
        <td class="nt-cell-body">${glyphSvg(h.body, 17)}<span>${esc(bodyName(h.body))}</span></td>
        <td><strong>${esc(t('star_' + h.star))}</strong></td>
        <td>${esc(fmtFull(h.lon))}</td>
        <td class="nt-num">${h.orb.toFixed(2)}°</td>
        <td class="nt-num nt-dim">${h.mag.toFixed(2)}</td>
    </tr>`).join('');
    return `<table class="nt-table nt-table-compact"><thead><tr>
        <th>${esc(t('col_body'))}</th><th>${esc(t('col_star'))}</th>
        <th>${esc(t('col_position'))}</th><th>${esc(t('col_orb'))}</th>
        <th>${esc(t('col_magnitude'))}</th></tr></thead><tbody>${rows}</tbody></table>
        <p class="nt-method">${esc(t('stars_method'))}</p>`;
}

function renderHouses(chart) {
    if (!chart.cusps) return `<p class="nt-dim">${esc(t('houses_need_time'))}</p>`;
    const deg = chart.houses?.degraded;
    const warn = deg
        ? `<div class="nt-notice nt-notice-warn"><strong>${esc(t('houses_degraded_title'))}</strong>
           <p>${esc(t('houses_degraded_' + (chart.houses.reason === 'polar' ? 'polar' : 'undefined'), {
            system: t('sys_' + chart.houses.requested),
        }))}</p></div>` : '';
    const rows = chart.cusps.map((c, i) =>
        `<tr><td class="nt-num">${i + 1}</td><td>${esc(fmtFull(c))}</td></tr>`).join('');
    return warn + `<table class="nt-table nt-table-compact"><thead><tr>
        <th>${esc(t('col_cusp'))}</th><th>${esc(t('col_position'))}</th></tr></thead><tbody>${rows}</tbody></table>`;
}

function renderAspects(chart) {
    if (!chart.aspects.length) return `<p class="nt-dim">${esc(t('no_aspects'))}</p>`;
    const rows = chart.aspects.filter(a => a.major || state.settings.minorAspects).map(a =>
        `<tr>
            <td class="nt-cell-body">${glyphSvg(a.a, 16)}<span>${esc(bodyName(a.a))}</span></td>
            <td class="nt-cell-body nt-asp-${a.type}">${glyphSvg(a.type, 16)}<span>${esc(t('asp_' + a.type))}</span></td>
            <td class="nt-cell-body">${glyphSvg(a.b, 16)}<span>${esc(bodyName(a.b))}</span></td>
            <td class="nt-num">${a.orb.toFixed(2)}°</td>
            <td class="nt-dim">${esc(a.applying ? t('asp_applying') : t('asp_separating'))}</td>
        </tr>`).join('');
    return `<table class="nt-table"><thead><tr>
        <th>${esc(t('col_body'))}</th><th>${esc(t('col_aspect'))}</th><th>${esc(t('col_body'))}</th>
        <th>${esc(t('col_orb'))}</th><th></th></tr></thead><tbody>${rows}</tbody></table>`;
}

function renderBalance(chart) {
    const b = chart.balance;
    const bar = (label, val, total) => {
        const pct = total ? Math.round(val / total * 100) : 0;
        return `<div class="nt-bar"><span class="nt-bar-label">${esc(label)}</span>
            <span class="nt-bar-track"><span class="nt-bar-fill" style="width:${pct}%"></span></span>
            <span class="nt-bar-val">${pct}%</span></div>`;
    };
    /* Wrapped in one grid per column so every label in a section shares an
     * auto-sized track. A fixed label width was 74px and "Фиксированный" is
     * ~90px, so the word ran straight over its own progress bar — and it would
     * do the same in German. max-content cannot overflow in any language. */
    const el = '<div class="nt-bars">'
        + Object.entries(b.elements).map(([k, v]) => bar(t('el_' + k), v, b.total)).join('') + '</div>';
    const mo = '<div class="nt-bars">'
        + Object.entries(b.modalities).map(([k, v]) => bar(t('mod_' + k), v, b.total)).join('') + '</div>';
    const patterns = chart.patterns.length
        ? `<p>${chart.patterns.map(p => `<span class="nt-tag">${esc(t('pat_' + p.type))}${p.bodies
            ? ': ' + p.bodies.map(bodyName).join(', ') : ''}</span>`).join(' ')}</p>` : '';

    const pol = b.polarity
        ? '<div class="nt-bars">'
            + bar(t('pol_positive'), b.polarity.positive, b.total)
            + bar(t('pol_negative'), b.polarity.negative, b.total) + '</div>'
        : '';

    /* One flowing grid rather than fixed pairs of columns. Hemispheres and
     * quadrants exist only when a birth time was given, and in a rigid two-up
     * layout their absence left the polarity block sitting beside a hole. */
    const blocks = [
        [t('elements'), el],
        [t('modalities'), mo],
        [t('polarity'), pol],
    ];
    if (b.placedTotal) {
        const h = b.hemispheres, q = b.quadrants;
        blocks.push([t('hemispheres'), '<div class="nt-bars">'
            + bar(t('hemi_east'), h.east, b.placedTotal)
            + bar(t('hemi_west'), h.west, b.placedTotal)
            + bar(t('hemi_north'), h.north, b.placedTotal)
            + bar(t('hemi_south'), h.south, b.placedTotal) + '</div>']);
        blocks.push([t('quadrants'), '<div class="nt-bars">'
            + bar(t('quad_1'), q.q1, b.placedTotal)
            + bar(t('quad_2'), q.q2, b.placedTotal)
            + bar(t('quad_3'), q.q3, b.placedTotal)
            + bar(t('quad_4'), q.q4, b.placedTotal) + '</div>']);
    }

    return `<div class="nt-balgrid">`
        + blocks.map(([h, body]) => `<div><h4>${esc(h)}</h4>${body}</div>`).join('')
        + `</div>
        <p class="nt-dim" style="margin-top:14px">${esc(t('chart_shape'))}: <strong>${esc(t('shape_' + chart.shape))}</strong>
        ${chart.sect ? ' · ' + esc(chart.sect.diurnal ? t('sect_day') : t('sect_night')) : ''}
        ${chart.ruler ? ' · ' + esc(t('chart_ruler')) + ': ' + esc(bodyName(chart.ruler)) : ''}</p>${patterns}`;
}

/* ── aspect grid ────────────────────────────────────────────────────────────
 *
 * The triangular matrix every reference site leads with. The flat list answers
 * "what aspects are there"; the grid answers "does anything touch Saturn", which
 * is the question people actually scan a chart for. Same data, and the shape is
 * what makes it readable.
 */
function renderAspectGrid(chart) {
    const keys = ['sun', 'moon', 'mercury', 'venus', 'mars', 'jupiter', 'saturn',
        'uranus', 'neptune', 'pluto', 'chiron', 'northNode', 'asc', 'mc']
        .filter(k => chart.points[k]);
    if (keys.length < 2) return '';

    const find = (a, b) => chart.aspects.find(s =>
        (s.a === a && s.b === b) || (s.a === b && s.b === a));

    /* The first row and the last column of a triangular matrix can never hold a
     * cell, so they are dropped rather than drawn as blank space. */
    let head = '<tr><th></th>';
    for (const k of keys.slice(0, -1)) head += `<th title="${esc(bodyName(k))}">${glyphSvg(k, 15)}</th>`;
    head += '</tr>';

    let body = '';
    for (let i = 1; i < keys.length; i++) {
        body += `<tr><th title="${esc(bodyName(keys[i]))}">${glyphSvg(keys[i], 15)}</th>`;
        for (let j = 0; j < keys.length - 1; j++) {
            if (j >= i) { body += '<td class="nt-ag-empty"></td>'; continue; }
            const a = find(keys[i], keys[j]);
            if (!a || (!a.major && !state.settings.minorAspects)) { body += '<td></td>'; continue; }
            const label = `${bodyName(keys[i])} ${t('asp_' + a.type)} ${bodyName(keys[j])} — `
                + `${t('col_orb')} ${a.orb.toFixed(2)}°`;
            body += `<td class="nt-ag-cell nt-asp-${a.type}" title="${esc(label)}">`
                + `${glyphSvg(a.type, 14)}<span class="nt-ag-orb">${a.orb.toFixed(1)}</span></td>`;
        }
        body += '</tr>';
    }
    return `<div class="nt-gridwrap"><table class="nt-aspectgrid">${head}${body}</table></div>`;
}

function renderWorking(chart) {
    const w = chart.working;
    const rows = [
        [t('w_jd'), w.julianDay.toFixed(6)],
        [t('w_obliquity'), w.obliquity.toFixed(6) + '°'],
        [t('w_gst'), w.gst.toFixed(4) + '°'],
        [t('w_ramc'), w.ramc.toFixed(4) + '°'],
        [t('w_utc'), chart.date.toISOString().replace('T', ' ').slice(0, 19) + ' UTC'],
        [t('w_coords'), `${chart.lat.toFixed(4)}, ${chart.lon.toFixed(4)}`],
        [t('w_zodiac'), chart.zodiac === 'sidereal'
            ? `${t('zodiac_sidereal')} · ${AYANAMSHAS[chart.ayanamsa]?.label} ${chart.ayanamsaValue.toFixed(4)}°`
            : t('zodiac_tropical')],
        [t('w_system'), t('sys_' + chart.system)],
    ];
    if (w.ascSpeedDegPerMin) {
        rows.push([t('w_ascrate'), t('w_ascrate_val', { min: (1 / w.ascSpeedDegPerMin).toFixed(2) })]);
    }
    return `<table class="nt-table nt-table-compact">${rows.map(([a, b]) =>
        `<tr><td>${esc(a)}</td><td class="nt-num">${esc(b)}</td></tr>`).join('')}</table>
        <p class="nt-dim">${esc(t('w_note'))}</p>`;
}

function renderSystemCompare(chart) {
    const all = allSystems(chart.date, chart.lat, chart.lon);
    const rows = HOUSE_SYSTEMS.map(s => {
        const r = all.systems[s];
        if (!r.cusps) return `<tr><td>${esc(t('sys_' + s))}</td><td colspan="4" class="nt-dim">${esc(t('sys_undefined'))}</td></tr>`;
        const mark = r.degraded ? ` <span class="nt-dim">(${esc(t('sys_fellback'))})</span>` : '';
        return `<tr${s === chart.system ? ' class="is-current"' : ''}>
            <td>${esc(t('sys_' + s))}${mark}</td>
            <td>${esc(fmtFull(r.cusps[0]))}</td>
            <td>${esc(fmtFull(r.cusps[9]))}</td>
            <td>${esc(fmtFull(r.cusps[10]))}</td>
            <td>${esc(fmtFull(r.cusps[1]))}</td></tr>`;
    }).join('');
    return `<p class="nt-dim">${esc(t('sys_compare_note'))}</p>
        <table class="nt-table nt-table-compact"><thead><tr>
        <th>${esc(t('col_system'))}</th><th>${esc(t('angle_asc'))}</th><th>${esc(t('angle_mc'))}</th>
        <th>11</th><th>2</th></tr></thead><tbody>${rows}</tbody></table>`;
}

/* The single most common question in this segment: another site shows me a
 * different Ascendant. Rather than argue, we test the hypotheses. */
function renderDiagnostic(chart) {
    return `<p>${esc(t('diag_intro'))}</p>
        <div class="nt-inline">
            <input type="text" id="nt-diag-asc" class="nt-input" placeholder="${esc(t('diag_placeholder'))}" inputmode="decimal">
            <button type="button" class="nt-btn nt-btn-sm" id="nt-diag-run">${esc(t('diag_run'))}</button>
        </div>
        <div id="nt-diag-out"></div>`;
}

function runDiagnostic(chart) {
    const raw = $('#nt-diag-asc').value.trim();
    const out = $('#nt-diag-out');
    const parsed = parseDegreeInput(raw);
    if (parsed == null) { out.innerHTML = `<p class="nt-warn">${esc(t('diag_parse_fail'))}</p>`; return; }

    const found = [];
    const all = allSystems(chart.date, chart.lat, chart.lon);
    for (const s of HOUSE_SYSTEMS) {
        const r = all.systems[s];
        if (!r.cusps) continue;
        if (Math.abs(angleDiff(r.cusps[0], parsed)) < 0.5) found.push(t('diag_hit_system', { system: t('sys_' + s) }));
    }
    // sidereal?
    for (const a of Object.keys(AYANAMSHAS)) {
        const sid = norm360(chart.angles.asc - (chart.ayanamsaValue || 0) - ayan(a, chart.date));
        if (Math.abs(angleDiff(sid, parsed)) < 1.0) found.push(t('diag_hit_sidereal', { name: AYANAMSHAS[a].label }));
    }
    // whole-hour timezone slip — the classic historical-offset mistake
    for (const dh of [-3, -2, -1, 1, 2, 3]) {
        const alt = buildAnglesAt(chart, dh * 3600000);
        if (Math.abs(angleDiff(alt, parsed)) < 1.0) {
            found.push(t('diag_hit_offset', { hours: (dh > 0 ? '+' : '') + dh }));
        }
    }
    out.innerHTML = found.length
        ? `<ul class="nt-list">${found.map(f => `<li>${esc(f)}</li>`).join('')}</ul>`
        : `<p class="nt-dim">${esc(t('diag_no_hit'))}</p>`;
}

/* ── forward rectification ──────────────────────────────────────────────────
 *
 * The diagnostic below answers "another site gave me a different Ascendant —
 * why". This answers the question people ask far more often and no free tool
 * answers: "I was told I'm Scorpio rising — what birth time would that mean?"
 *
 * The Ascendant sweeps the whole circle in a day, so a sign occupies one
 * contiguous window of roughly two hours, moved by latitude. It is found by
 * scanning the civil day rather than solved analytically: the rate is not
 * constant, the day may contain a DST jump, and a scan cannot silently return a
 * window that does not exist at this latitude.
 */
function ascendantWindows(signIndex, y, mo, d, place) {
    const { angles } = window.__NATAL_HOUSES || {};
    if (!angles) return [];
    const spans = [];
    let open = null;
    const STEP = 2;                                   // minutes
    for (let m = 0; m <= 24 * 60; m += STEP) {
        const r = localToUtc(place.tz, y, mo, d, Math.floor(m / 60) % 24, m % 60);
        const ms = r.instants && r.instants[0];
        // A skipped hour has no instant at all; treat it as a break in the run.
        if (!ms) { if (open !== null) { spans.push([open, m - STEP]); open = null; } continue; }
        const asc = angles(new Date(ms), place.lat, place.lng).asc;
        const inSign = Math.floor(norm360(asc) / 30) === signIndex;
        if (inSign && open === null) open = m;
        if (!inSign && open !== null) { spans.push([open, m - STEP]); open = null; }
    }
    if (open !== null) spans.push([open, 24 * 60]);
    const hhmm = (m) => `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    return spans.map(([a, b]) => ({ fromMin: a, toMin: b, from: hhmm(a), to: hhmm(b) }));
}

async function runRectify() {
    const out = $('#nt-rect-out');
    if (!out) return;
    const sel = $('#nt-rect-sign');
    const signIndex = sel ? +sel.value : -1;
    const dv = $('#nt-date')?.value;
    if (!dv || !state.place || signIndex < 0) {
        out.innerHTML = `<p class="nt-warn">${esc(t('rect_need_data'))}</p>`;
        return;
    }
    /* The scan walks the whole day through the same house model that builds the
     * chart, which takes a couple of seconds. Without a busy state the button
     * looks dead and gets pressed again, queueing a second scan behind the
     * first. Yielding a frame first lets the message actually paint. */
    const btn = $('#nt-rect-run');
    if (btn) { btn.disabled = true; }
    out.innerHTML = `<p class="nt-dim">${esc(t('rect_working'))}</p>`;
    await new Promise(r => requestAnimationFrame(() => setTimeout(r, 0)));

    const [y, mo, d] = dv.split('-').map(Number);
    let spans;
    try { spans = ascendantWindows(signIndex, y, mo, d, state.place); }
    finally { if (btn) btn.disabled = false; }
    if (!spans.length) {
        out.innerHTML = `<p class="nt-dim">${esc(t('rect_none', { sign: signName(signIndex) }))}</p>`;
        return;
    }
    out.innerHTML = `<p>${esc(t('rect_found', { sign: signName(signIndex) }))}</p>`
        + `<ul class="nt-list">` + spans.map(s =>
            `<li><strong>${esc(s.from)} — ${esc(s.to)}</strong>
             <button type="button" class="nt-linkbtn" data-t="${esc(s.from)}">${esc(t('rect_use'))}</button></li>`).join('')
        + `</ul><p class="nt-method">${esc(t('rect_method'))}</p>`;
    $$('.nt-linkbtn', out).forEach(b => b.addEventListener('click', () => {
        $('#nt-time').value = b.dataset.t;
        $('#nt-unknown-time').checked = false;
        $('#nt-time').disabled = false;
        run();
    }));
}

function ayan(name, date) {
    const a = AYANAMSHAS[name];
    const years = (date.getTime() - Date.UTC(2000, 0, 1, 12)) / (365.25 * 86400000);
    return a.j2000 + years * (50.2879 / 3600);
}

function buildAnglesAt(chart, deltaMs) {
    // Cheap: only the Ascendant is needed for the hypothesis test.
    const { angles } = window.__NATAL_HOUSES || {};
    return angles
        ? angles(new Date(chart.date.getTime() + deltaMs), chart.lat, chart.lon).asc
        : NaN;
}

function parseDegreeInput(raw) {
    if (!raw) return null;
    // "12°34' Лев" / "12 34 leo" / "132.57"
    const plain = parseFloat(raw.replace(',', '.'));
    const signIdx = SIGN_KEYS.findIndex(k => {
        const nm = t('sign_' + k).toLowerCase();
        return raw.toLowerCase().includes(nm.slice(0, 4)) || raw.toLowerCase().includes(k.slice(0, 4));
    });
    const m = raw.match(/(\d{1,2})\s*[°º:\s]\s*(\d{1,2})?/);
    if (signIdx >= 0 && m) {
        return norm360(signIdx * 30 + (+m[1]) + (m[2] ? +m[2] / 60 : 0));
    }
    if (Number.isFinite(plain) && !signIdx && plain >= 0 && plain <= 360) return norm360(plain);
    if (Number.isFinite(plain) && plain >= 0 && plain <= 360) return norm360(plain);
    return null;
}

/* ── main run ───────────────────────────────────────────────────────────── */

async function run() {
    const res = $('#nt-results');
    if (!$('#nt-date').value) { flash(t('err_no_date')); return; }
    // Typed but not clicked is the normal case, not an error. Resolve it here
    // rather than refusing; resolveTypedPlace only comes back false when the
    // name is unknown or genuinely ambiguous, and it explains which.
    if (!state.place && !(await resolveTypedPlace())) return;
    if (!state.place) return;

    const r = resolveTime();
    state.resolved = r;
    renderTimeNotice(r);
    if (!r) return;

    if (r.kind === 'gap') { res.hidden = true; return; }

    let instant;
    if (r.kind === 'unknown-time') {
        instant = new Date(Date.UTC(r.y, r.mo - 1, r.d, 12));
    } else {
        instant = new Date(
            r.kind === 'ambiguous'
                ? (state.chosenInstant && r.instants.includes(state.chosenInstant)
                    ? state.chosenInstant : r.instants[0])
                : r.instants[0]);
        if (r.kind === 'ambiguous' && !state.chosenInstant) state.chosenInstant = r.instants[0];
    }

    const withHouses = r.kind !== 'unknown-time';
    const opts = {
        system: state.settings.system,
        zodiac: state.settings.zodiac,
        ayanamsa: state.settings.ayanamsa,
        orbScale: state.settings.orbScale,
        modernRulers: state.settings.modernRulers,
        /* Both branches used to read `undefined`, so findAspects always fell
         * back to the five majors: the switch was inert, six aspect types with
         * finished glyphs and translations were unreachable, and the yod — which
         * is defined by two quincunxes — could never be detected. */
        aspectSet: state.settings.minorAspects ? Object.keys(ASPECTS) : undefined,
        withHouses,
    };

    res.setAttribute('aria-busy', 'true');
    try {
        const precision = withHouses
            ? PRECISIONS[$('#nt-precision').value] ?? 0
            : 0;
        let unc;
        if (withHouses) {
            unc = await assess(instant, state.place.lat, state.place.lng, precision, opts);
        } else {
            /* Unknown time is the tool's own thesis, and it was the one mode
             * that shipped without it: a noon chart was drawn and every badge
             * omitted, so the Moon — which crosses a sign on nearly half of all
             * days — was presented as confidently as the Sun. Scan the whole
             * day instead and mark what actually holds. */
            const base = await buildChart(instant, state.place.lat, state.place.lng, opts);
            const dayStart = new Date(Date.UTC(r.y, r.mo - 1, r.d, 0));
            const summary = await unknownTimeSummary(dayStart, state.place.lat, state.place.lng, opts);
            const points = {};
            for (const [k, v] of Object.entries(summary.points)) {
                points[k] = {
                    ...v,
                    verdict: v.signStable ? VERDICT.STABLE : VERDICT.BOUNDARY,
                };
            }
            unc = { base, points, angles: {}, window: 0, unknownTime: true };
        }

        state.chart = unc.base;
        state.uncertainty = unc;
        paint(unc);
        window.__aeToolComplete?.({ system: state.settings.system, precision: $('#nt-precision')?.value });
    } catch (err) {
        window.__handleError?.(err, SLUG);
        flash(t('err_failed'));
    } finally {
        res.removeAttribute('aria-busy');
    }
}

/**
 * Вынос колеса за поля панели на узком экране.
 *
 * Считается ЗАМЕРОМ, а не константой: между колесом и краем экрана лежат два
 * отступа — панели тула и контейнера страницы, — и второй нам не принадлежит.
 * Колесо круглое, поэтому ширина здесь напрямую переводится в кегль каждой
 * подписи внутри: 320 → 390 px это +22 % ко всем числам на карте.
 * ⛔ `width:100vw` вместо этого не годится: в узком окне десктопа 100vw шире
 * клиентской области на полосу прокрутки, и страница начинает ехать вбок.
 */
function fitWheelBleed() {
    const box = document.getElementById('nt-wheel');
    if (!box) return;
    box.style.removeProperty('--nt-bleed');
    if (!window.matchMedia('(max-width: 560px)').matches) return;
    const bleed = Math.floor((document.documentElement.clientWidth - box.getBoundingClientRect().width) / 2);
    if (bleed > 0) box.style.setProperty('--nt-bleed', bleed + 'px');
}

let wheelBleedTimer = 0;
window.addEventListener('resize', () => {
    clearTimeout(wheelBleedTimer);
    wheelBleedTimer = setTimeout(fitWheelBleed, 120);
});

function paint(unc) {
    const chart = unc.base;
    const res = $('#nt-results');
    res.hidden = false;

    fitWheelBleed();
    fill('#nt-wheel', renderWheel(chart, {
        size: 720,
        minorAspects: state.settings.minorAspects,
        title: t('wheel_title'),
        desc: t('wheel_desc'),
        caption: { lines: captionLines(chart) },
        labelOf: (key, p) => `${bodyName(key)} — ${fmtFull(p.lon)}`
            + (p.house ? ` · ${t('col_house')} ${p.house}` : '')
            + (p.retrograde ? ' · R' : ''),
    }));

    fill('#nt-summary', renderBigThree(chart, unc));
    fill('#nt-facts', renderFacts(chart));

    const uncEl = $('#nt-uncert');
    if (uncEl && unc.window > 0) {
        const el = uncEl;
        const a = unc.angles.asc;
        el.hidden = false;
        el.innerHTML = `<h3>${esc(t('unc_title'))}</h3>
            <p>${esc(t('unc_body', {
                window: unc.window,
                span: a ? a.spanDeg.toFixed(2) : '—',
                rate: unc.ascMinutesPerDegree ? unc.ascMinutesPerDegree.toFixed(1) : '—',
            }))}</p>
            ${a && a.signs.length > 1
                ? `<div class="nt-notice nt-notice-warn">${esc(t('unc_asc_split', { signs: a.signs.map(s => t('sign_' + s)).join(' / ') }))}</div>`
                : ''}`;
    } else if (uncEl) {
        uncEl.hidden = true;
    }

    fill('#nt-positions', renderPositions(chart, unc));
    fill('#nt-houses', renderHouses(chart));
    fill('#nt-aspects', renderAspects(chart));
    fill('#nt-aspectgrid', renderAspectGrid(chart));
    fill('#nt-dignities', renderDignities(chart));
    fill('#nt-stars', renderStars(chart));
    fill('#nt-balance', renderBalance(chart));
    fill('#nt-working', renderWorking(chart));
    if (chart.cusps) {
        fill('#nt-compare', renderSystemCompare(chart));
        fill('#nt-diagnostic', renderDiagnostic(chart));
        $('#nt-diag-run')?.addEventListener('click', () => runDiagnostic(chart));
    }
    const showExtras = !!chart.cusps;
    if ($('#nt-compare-wrap')) $('#nt-compare-wrap').hidden = !showExtras;
    if ($('#nt-diagnostic-wrap')) $('#nt-diagnostic-wrap').hidden = !showExtras;

    /* The reading arrives after the chart: it needs a second fetch for the
     * locale's vocabulary, and the numbers must not wait on prose. Failures are
     * reported rather than swallowed — a silently hidden section looks
     * identical to a tool that never had the feature. */
    const interpEl = $('#nt-interpret');
    if (interpEl) {
        interpEl.dataset.state = 'loading';
        import('./interpret.js')
            .then(m => m.renderInterpretation(interpEl, chart, unc, t))
            .then(() => { interpEl.dataset.state = 'ready'; })
            .catch(err => {
                interpEl.dataset.state = 'failed';
                interpEl.hidden = true;
                window.__handleError?.(err, SLUG + ':interpret');
            });
    }

    res.scrollIntoView({ block: 'nearest' });
}

/* Birth data as caption lines for the wheel. Kept here rather than in wheel.js
 * because it is the page that knows the entered name, the resolved place and the
 * locale's date formatting. */
function captionLines(chart) {
    const name = ($('#nt-name')?.value || '').trim();
    const place = state.place?.name || '';
    const d = $('#nt-date')?.value || '';
    const unknown = $('#nt-unknown-time')?.checked;
    const time = unknown ? t('tn_unknown_title') : ($('#nt-time')?.value || '');
    const when = [d.split('-').reverse().join('.'), time].filter(Boolean).join(' · ');
    const sys = `${t('sys_' + chart.system)} · ${chart.zodiac === 'sidereal'
        ? `${t('zodiac_sidereal')} (${AYANAMSHAS[chart.ayanamsa]?.label})` : t('zodiac_tropical')}`;
    return [name || null, [when, place].filter(Boolean).join(' · '), sys];
}

function flash(msg) { window.__toast ? window.__toast(msg, 'warning') : alert(msg); }

/* ── settings + wiring ──────────────────────────────────────────────────── */

function initSettings() {
    const bind = (id, key, cast = (v) => v) => {
        const el = $(id);
        if (!el) return;
        window.__toolSettings?.bind(SLUG, el, key, state.settings[key]);
        const apply = () => {
            state.settings[key] = cast(el.type === 'checkbox' ? el.checked : el.value);
            if (state.chart) run();
        };
        el.addEventListener('change', apply);
        state.settings[key] = cast(el.type === 'checkbox' ? el.checked : el.value);
    };
    bind('#nt-system', 'system');
    bind('#nt-zodiac', 'zodiac');
    bind('#nt-ayanamsa', 'ayanamsa');
    bind('#nt-orb', 'orbScale', Number);
    bind('#nt-minor', 'minorAspects');
    bind('#nt-rulers', 'modernRulers');

    const syncAyan = () => { $('#nt-ayanamsa-row').hidden = $('#nt-zodiac').value !== 'sidereal'; };
    $('#nt-zodiac')?.addEventListener('change', syncAyan);
    syncAyan();
}

function initForm() {
    const unknown = $('#nt-unknown-time');
    const sync = () => {
        const off = unknown.checked;
        $('#nt-time').disabled = off;
        $('#nt-precision').disabled = off;
        $('#nt-time-row').classList.toggle('is-off', off);
    };
    unknown?.addEventListener('change', sync);
    sync();

    $('#nt-form')?.addEventListener('submit', (e) => { e.preventDefault(); state.chosenInstant = null; run(); });

    /* A real tab widget: the markup declared role="tablist" while the buttons
     * carried no role, no aria-selected and no arrow-key handling, which is an
     * ARIA contract a screen reader cannot make sense of. */
    const tabs = $$('.nt-tabs button');
    const select = (b) => {
        tabs.forEach(x => {
            const on = x === b;
            x.classList.toggle('is-on', on);
            x.setAttribute('aria-selected', on ? 'true' : 'false');
            x.tabIndex = on ? 0 : -1;
        });
        $$('.nt-tabpanel').forEach(p => { p.hidden = p.dataset.tab !== b.dataset.tab; });
    };
    tabs.forEach((b, i) => {
        b.setAttribute('role', 'tab');
        b.id = b.id || `nt-tab-${b.dataset.tab}`;
        b.setAttribute('aria-controls', `nt-panel-${b.dataset.tab}`);
        b.addEventListener('click', () => select(b));
        b.addEventListener('keydown', (e) => {
            const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
            if (!dir) return;
            e.preventDefault();
            const next = tabs[(i + dir + tabs.length) % tabs.length];
            select(next); next.focus();
        });
    });
    $$('.nt-tabpanel').forEach(p => {
        p.setAttribute('role', 'tabpanel');
        p.id = `nt-panel-${p.dataset.tab}`;
        p.setAttribute('aria-labelledby', `nt-tab-${p.dataset.tab}`);
    });
    if (tabs[0]) select(tabs[0]);

    /* Printing used to lose two of the three tables and every collapsed panel:
     * the inactive tab panels carry `hidden` and the details blocks are closed,
     * so the paper copy held the wheel and one table. Before the print dialog
     * everything is opened, and afterwards the screen state is restored. */
    let restore = null;
    window.addEventListener('beforeprint', () => {
        const panels = $$('.nt-tabpanel').filter(p => p.hidden);
        const details = $$('.nt-wrap details').filter(d => !d.open);
        // Every panel carries the label of its own tab, so the printed copy says
        // which table is which instead of running four of them together.
        $$('.nt-tabpanel').forEach(p => {
            const tab = document.querySelector(`.nt-tabs button[data-tab="${p.dataset.tab}"]`);
            if (tab) p.dataset.printLabel = tab.textContent.trim();
        });
        panels.forEach(p => { p.hidden = false; });
        details.forEach(d => { d.open = true; });
        document.querySelector('#nt-results')?.setAttribute('data-printing', '1');
        restore = () => {
            panels.forEach(p => { p.hidden = true; });
            details.forEach(d => { d.open = false; });
            document.querySelector('#nt-results')?.removeAttribute('data-printing');
        };
    });
    window.addEventListener('afterprint', () => { restore?.(); restore = null; });
}

/* ── shareable link ─────────────────────────────────────────────────────
 *
 * The birth data rides in the URL FRAGMENT, never the query string. A fragment
 * is not sent to the server, so a shared chart link cannot deposit somebody's
 * date, time and place of birth into our access logs, our page cache or
 * Cloudflare's. The link still works for whoever opens it — the page reads the
 * fragment client-side and rebuilds the chart.
 */
function shareFragment() {
    const p = state.place;
    if (!p) return '';
    const q = new URLSearchParams({
        d: $('#nt-date').value,
        t: $('#nt-unknown-time').checked ? '' : $('#nt-time').value,
        a: $('#nt-precision').value,
        lat: p.lat.toFixed(4), lng: p.lng.toFixed(4), tz: p.tz, n: p.name,
        s: state.settings.system, z: state.settings.zodiac,
    });
    const who = ($('#nt-name')?.value || '').trim();
    if (who) q.set('w', who);
    return '#' + q.toString();
}

function applyShareFragment() {
    const raw = location.hash.replace(/^#/, '');
    if (!raw) return false;
    const q = new URLSearchParams(raw);
    if (!q.get('d') || !q.get('lat')) return false;

    $('#nt-date').value = q.get('d');
    const time = q.get('t');
    $('#nt-unknown-time').checked = !time;
    if (time) $('#nt-time').value = time;
    if (q.get('a')) $('#nt-precision').value = q.get('a');
    if (q.get('s')) { $('#nt-system').value = q.get('s'); state.settings.system = q.get('s'); }
    if (q.get('z')) { $('#nt-zodiac').value = q.get('z'); state.settings.zodiac = q.get('z'); }
    if (q.get('w') && $('#nt-name')) $('#nt-name').value = q.get('w');

    pickPlace({
        kind: 'city', name: q.get('n') || '—',
        lat: +q.get('lat'), lng: +q.get('lng'), tz: q.get('tz'),
        cc: '', region: '',
    });
    return true;
}

/* ── saved charts ───────────────────────────────────────────────────────────
 *
 * People come back to a chart tool with the same handful of birth data — their
 * own, a partner's, a child's — and re-typing date, time and city each visit is
 * the friction that sends them to a site that remembers.
 *
 * Stored as the SAME fragment string the share link already produces, in
 * localStorage only. That keeps the tool's existing stance intact: birth data
 * never reaches a server, not even ours, so this adds convenience without
 * quietly turning the tool into a place where dates of birth accumulate.
 */
const SAVED_KEY = 'natal:saved';
const MAX_SAVED = 20;

function loadSaved() {
    try {
        const raw = JSON.parse(localStorage.getItem(SAVED_KEY) || '[]');
        return Array.isArray(raw) ? raw.filter(x => x && x.frag) : [];
    } catch { return []; }
}

function writeSaved(list) {
    try { localStorage.setItem(SAVED_KEY, JSON.stringify(list.slice(0, MAX_SAVED))); }
    catch { /* private mode or full quota — the tool still works, just forgets */ }
}

function renderSaved() {
    const box = $('#nt-saved');
    if (!box) return;
    const list = loadSaved();
    if (!list.length) { box.hidden = true; box.innerHTML = ''; return; }
    box.hidden = false;
    box.innerHTML = `<div class="nt-saved-head">${esc(t('saved_title'))}</div>`
        + `<div class="nt-saved-list">` + list.map((s, i) => `
            <span class="nt-saved-item">
                <button type="button" class="nt-saved-open" data-i="${i}">${esc(s.label)}</button>
                <button type="button" class="nt-saved-del" data-del="${i}"
                        aria-label="${esc(t('saved_remove', { name: s.label }))}">×</button>
            </span>`).join('') + '</div>';

    $$('.nt-saved-open', box).forEach(b => b.addEventListener('click', () => {
        const s = loadSaved()[+b.dataset.i];
        if (!s) return;
        location.hash = s.frag;
        if (applyShareFragment()) run();
    }));
    $$('.nt-saved-del', box).forEach(b => b.addEventListener('click', () => {
        const list2 = loadSaved();
        list2.splice(+b.dataset.del, 1);
        writeSaved(list2);
        renderSaved();
    }));
}

function initSaved() {
    renderSaved();
    $('#nt-save')?.addEventListener('click', () => {
        const frag = shareFragment();
        if (!frag) return;
        const who = ($('#nt-name')?.value || '').trim();
        const label = who || `${state.place?.name || ''} ${$('#nt-date')?.value || ''}`.trim() || '—';
        const list = loadSaved().filter(s => s.frag !== frag);
        list.unshift({ label: label.slice(0, 40), frag });
        writeSaved(list);
        renderSaved();
        window.__toast?.(t('saved_done'), 'success');
    });
}

function initShare() {
    $('#nt-share')?.addEventListener('click', async () => {
        const frag = shareFragment();
        if (!frag) return;
        const url = location.origin + location.pathname + frag;
        history.replaceState(null, '', frag);
        try {
            if (navigator.share) { await navigator.share({ url, title: t('wheel_title') }); return; }
            await navigator.clipboard.writeText(url);
            window.__toast?.(t('share_copied'), 'success');
        } catch {
            // Clipboard can be refused (permissions, insecure context). Show the
            // link so it can still be copied by hand instead of failing mutely.
            const box = $('#nt-share-out');
            box.hidden = false;
            box.querySelector('input').value = url;
            box.querySelector('input').select();
        }
    });
}

/* Портативная копия колеса. Вся механика — сбор стилей, заморозка токенов,
 * подложка — живёт в общем модуле колеса: тот же файл рисует колесо и для
 * /horary, и держать «как оно становится файлом» отдельно от «как оно
 * выглядит» значит заводить вторую правду. Здесь остаётся только то, что
 * принадлежит этому тулу: где взять колесо и что сказать, если не вышло. */
function standaloneSvg() {
    const out = standaloneWheelSvg($('#nt-wheel svg'));
    if (!out) {
        /* Отказ лучше выдачи. Колесо без стилей — это не ухудшенная картинка,
         * а чёрный диск, и человек узнаёт об этом уже после сохранения. */
        window.__aeFunnel?.('export_css_missing', {});
        flash(t('err_failed'));
        return null;
    }
    return out;
}

function exportBaseName() {
    const name = ($('#nt-name')?.value || '').trim().replace(/[\\/:*?"<>|]+/g, '').slice(0, 40);
    const date = $('#nt-date')?.value || '';
    return ['natal-chart', name, date].filter(Boolean).join('-');
}

function initExport() {
    $('#nt-export-svg')?.addEventListener('click', () => {
        const svg = standaloneSvg();
        if (!svg) return;
        window.__downloadFile(new Blob([svg], { type: 'image/svg+xml' }), exportBaseName() + '.svg');
    });

    /* PNG is what actually gets posted and messaged; an SVG is opened by almost
     * nothing on a phone. Rasterised from the same standalone string at 2× so it
     * stays sharp when it is viewed full-screen. */
    $('#nt-export-png')?.addEventListener('click', () => {
        const svgText = standaloneSvg();
        if (!svgText) return;
        const el = $('#nt-wheel svg');
        const vb = (el.getAttribute('viewBox') || '0 0 720 720').split(/\s+/).map(Number);
        const w = vb[2] || 720, h = vb[3] || 720, scale = 2;
        const img = new Image();
        const url = URL.createObjectURL(new Blob([svgText], { type: 'image/svg+xml' }));
        img.onload = () => {
            const cv = document.createElement('canvas');
            cv.width = w * scale; cv.height = h * scale;
            const ctx = cv.getContext('2d');
            ctx.drawImage(img, 0, 0, cv.width, cv.height);
            URL.revokeObjectURL(url);
            cv.toBlob(b => { if (b) window.__downloadFile(b, exportBaseName() + '.png'); }, 'image/png');
        };
        img.onerror = () => { URL.revokeObjectURL(url); flash(t('err_failed')); };
        img.src = url;
    });

    $('#nt-export-csv')?.addEventListener('click', () => {
        const c = state.chart;
        if (!c) return;
        const q = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const lines = [];
        lines.push(['section', 'a', 'b', 'longitude', 'sign', 'degree', 'house', 'retrograde', 'speed', 'orb'].map(q).join(','));
        for (const [k, p] of Object.entries(c.points)) {
            const f = fmtLon(p.lon);
            lines.push(['position', bodyName(k), '', p.lon.toFixed(6), signName(f.sign), f.text,
                p.house ?? '', p.retrograde ? 'R' : '', (p.speed ?? 0).toFixed(5), ''].map(q).join(','));
        }
        // Cusps and aspects were absent from the export entirely, so the file
        // could not reproduce the chart it came from.
        (c.cusps || []).forEach((cusp, i) => {
            const f = fmtLon(cusp);
            lines.push(['cusp', String(i + 1), '', cusp.toFixed(6), signName(f.sign), f.text, '', '', '', ''].map(q).join(','));
        });
        for (const a of c.aspects) {
            if (!a.major && !state.settings.minorAspects) continue;
            lines.push(['aspect', bodyName(a.a), bodyName(a.b), '', t('asp_' + a.type), '', '', '', '', a.orb.toFixed(3)].map(q).join(','));
        }
        // BOM so Excel opens the Cyrillic body names as UTF-8 rather than mojibake.
        window.__downloadFile(new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' }),
            exportBaseName() + '.csv');
    });
}

document.addEventListener('DOMContentLoaded', async () => {
    // Expose the house module for the diagnostic's cheap Ascendant probe.
    window.__NATAL_HOUSES = await import('./houses.js');
    initPlacePicker();
    initForm();
    initSettings();
    initExport();
    initShare();
    primeWheelCss();
    initSaved();
    $('#nt-rect-run')?.addEventListener('click', runRectify);
    // A shared link rebuilds its chart straight away — the recipient should see
    // the chart, not an empty form they have to fill in again.
    if (applyShareFragment()) { window.__stage?.('opened_shared'); run(); }
    window.__stage?.('ready');
});
